import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { updateUserTraining } from 'api/Admin/Trainings/Trainings';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { WIZARD_PROGRESS } from '../../lib/enum';

import TrainingAttestationCard from './components/TrainingAttestationCard';
import TrainingDateCard from './components/TrainingDateCard';
import TrainingDetailsCard from './components/TrainingDetailsCard';
import { getTrainingConfig } from './lib/config';
import useLiveTrainingData from './lib/useLiveTrainingData';

/**
 * LiveTrainingStep - A shared component for live training wizard steps
 *
 * Displays training attestation (admin-only), date/time/link, attendees, and responsibilities
 * in a card-based layout. Admins can set attendance and confirmations which are submitted
 * via the orchestrator on Continue.
 */
const LiveTrainingStep = ({
  isAdmin,
  substepCode,
  addFooterActions,
  setHeaderButtonProps,
  onNext,
  mutateOrchestratorData,
  orchestratorData,
  orchestratorLoading,
  wizardState
}) => {
  const navigate = useNavigate();
  const isCompleted = orchestratorData?.handlerStatus === WIZARD_PROGRESS.COMPLETED;
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [confirmedItems, setConfirmedItems] = useState([]);
  const [hasInitializedAttendance, setHasInitializedAttendance] = useState(false);

  const config = useMemo(() => getTrainingConfig(substepCode), [substepCode]);

  const {
    trainingId,
    practiceId,
    formattedDateTime,
    trainingLink,
    attendees,
    currentAttendanceStatus,
    currentTrainingConfirmations
  } = useLiveTrainingData({
    orchestratorData,
    wizardState,
    meetingKey: config?.meetingKey
  });

  const { mutate: updateAttendance, isLoading: isUpdatingAttendance } = useCustomMutation({
    mutationFn: (params) => updateUserTraining(navigate, params),
    invalidateQueryKey: 'user-trainings',
    disableAlert: true
  });

  // Reset all state when navigating to a different substep
  useEffect(() => {
    setAttendanceStatus(null);
    setConfirmedItems(new Array(config?.attestationConfirmations?.length || 0).fill(false));
    setHasInitializedAttendance(false);
  }, [substepCode, config?.attestationConfirmations?.length]);

  // Initialize attendance and confirmations from DB once data is loaded
  useEffect(() => {
    if (!hasInitializedAttendance && currentAttendanceStatus) {
      setAttendanceStatus(currentAttendanceStatus);
      setHasInitializedAttendance(true);
    }

    if (!hasInitializedAttendance && currentTrainingConfirmations && config?.confirmationKeys) {
      const restored = config.confirmationKeys.map(
        (key) => currentTrainingConfirmations[key] === true
      );
      setConfirmedItems(restored);
    }
  }, [
    currentAttendanceStatus,
    currentTrainingConfirmations,
    hasInitializedAttendance,
    config?.confirmationKeys
  ]);

  // Build the trainingConfirmations object from current checkbox state
  const buildTrainingConfirmations = useCallback(
    (items) => {
      return (config?.confirmationKeys || []).reduce((acc, key, index) => {
        acc[key] = items[index] || false;
        return acc;
      }, {});
    },
    [config?.confirmationKeys]
  );

  // When attendance changes, update local state and persist to the backend
  const handleAttendanceChange = useCallback(
    (value) => {
      setAttendanceStatus(value);

      let resetItems = confirmedItems;
      if (value === 'did_not_attend') {
        resetItems = new Array(config?.attestationConfirmations?.length || 0).fill(false);
        setConfirmedItems(resetItems);
      }

      // Persist attendance status (and reset confirmations if did_not_attend) via API
      if (trainingId && practiceId) {
        updateAttendance({
          practiceId,
          id: trainingId,
          attendanceStatus: value,
          trainingConfirmations: buildTrainingConfirmations(resetItems)
        });
      }
    },
    [
      config?.attestationConfirmations?.length,
      confirmedItems,
      trainingId,
      practiceId,
      updateAttendance,
      buildTrainingConfirmations
    ]
  );

  const handleConfirmationChange = useCallback(
    (index) => {
      setConfirmedItems((prev) => {
        const next = [...prev];
        next[index] = !next[index];

        // Persist training confirmations to user_training record via API
        if (trainingId && practiceId) {
          updateAttendance({
            practiceId,
            id: trainingId,
            trainingConfirmations: buildTrainingConfirmations(next)
          });
        }

        return next;
      });
    },
    [trainingId, practiceId, updateAttendance, buildTrainingConfirmations]
  );

  useEffect(() => {
    setHeaderButtonProps(null);

    addFooterActions([
      {
        text: 'Continue',
        onClick: () => {
          if (!isAdmin) {
            return onNext();
          }
          if (isCompleted) return onNext();

          mutateOrchestratorData({});
        },
        isDisabled: orchestratorLoading
      }
    ]);
  }, [
    orchestratorLoading,
    isCompleted,
    addFooterActions,
    setHeaderButtonProps,
    onNext,
    mutateOrchestratorData
  ]);

  if (!config) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-neutral-500">Training configuration not found for this step.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-5">
      {isAdmin && (
        <TrainingAttestationCard
          attendanceStatus={attendanceStatus}
          onAttendanceChange={handleAttendanceChange}
          confirmations={config.attestationConfirmations}
          confirmedItems={confirmedItems}
          onConfirmationChange={handleConfirmationChange}
          isCompleted={isCompleted}
          isUpdating={isUpdatingAttendance}
        />
      )}
      <TrainingDateCard formattedDateTime={formattedDateTime} trainingLink={trainingLink} />
      <TrainingDetailsCard attendees={attendees} responsibilities={config.responsibilities} />
    </div>
  );
};

export default LiveTrainingStep;
